import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download, ArrowLeft, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";

const KIND_LABEL = {
  promotion: "Promotion",
  training: "Training",
};

export default function Certificates() {
  const [certs, setCerts] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const { level_names } = useSettings();

  useEffect(() => {
    api.get("/certificates").then((r) => setCerts(r.data)).catch(() => setCerts([]));
  }, []);

  const download = async (c) => {
    setDownloadingId(c.id);
    try {
      // Cache-bust with timestamp so the browser/CDN never serves a stale rendering
      const res = await api.get(`/certificates/${c.id}/download?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `crece-${c.kind}-${c.certificate_no || c.id}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  if (certs === null) return <div className="text-stone-500">Loading certificates...</div>;

  return (
    <div className="space-y-8" data-testid="certificates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/app/profile" className="inline-flex items-center text-xs font-bold text-stone-600 hover:text-emerald-900 mb-2" data-testid="certificates-back">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Profile
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Achievements</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">My Certificates</h1>
          <p className="text-stone-600 mt-2">Every rank you unlock and every training you complete is recorded here. Download anytime.</p>
        </div>
      </div>

      {certs.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center" data-testid="certificates-empty">
          <Sparkles className="w-10 h-10 mx-auto text-amber-500" />
          <div className="font-display text-xl font-black mt-3">No certificates yet</div>
          <p className="text-stone-500 text-sm mt-1 max-w-md mx-auto">
            Add downlines, hit your first rank milestone, and your first promotion certificate will be auto-issued here.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="certificates-grid">
          {certs.map((c) => {
            const isPromo = c.kind === "promotion";
            const headline = isPromo
              ? (c.rank_name || (level_names && level_names[(c.rank_index || 1) - 1]) || `Rank ${c.rank_index}`)
              : (c.course_name || "Training Programme");
            return (
              <div key={c.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden flex flex-col" data-testid={`certificate-card-${c.id}`}>
                <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-amber-50 p-6 relative">
                  <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_30%,rgba(217,119,6,0.4),transparent_60%)]" />
                  <div className="relative">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
                      {isPromo ? <Award className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                      {KIND_LABEL[c.kind] || "Achievement"} Certificate
                    </div>
                    <div className="font-display text-2xl font-black mt-2 leading-tight" data-testid={`certificate-headline-${c.id}`}>{headline}</div>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="text-xs text-stone-500">
                    <div className="flex justify-between">
                      <span>Cert No.</span>
                      <span className="font-mono font-bold text-stone-700" data-testid={`certificate-no-${c.id}`}>{c.certificate_no}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span>Issued</span>
                      <span className="font-mono text-stone-700">{c.issued_at ? new Date(c.issued_at).toLocaleDateString() : "—"}</span>
                    </div>
                    {isPromo && (
                      <div className="flex justify-between mt-1">
                        <span>Rank Index</span>
                        <span className="font-mono text-stone-700">L{c.rank_index}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    data-testid={`certificate-download-${c.id}`}
                    onClick={() => download(c)}
                    disabled={downloadingId === c.id}
                    className="mt-5 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold w-full">
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingId === c.id ? "Generating..." : "Download PNG"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
